import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useRoleNavigation } from "../../../public/hooks/useRoleNavigation";
import { NavRailIcon } from "../../../public/components/NavRailIcons";
import { decodeJwtPayload } from "../../../public/utils/jwtRole";
import { useEffectiveRoleSlug } from "../useEffectiveRoleSlug";
import "./DynamicRoleDashboard.css";

function collectScreensFromItem(item, out = []) {
  if (item?.to?.startsWith("/app/")) {
    out.push({ label: item.label, to: item.to, iconKey: item.iconKey });
  }
  if (Array.isArray(item?.children)) {
    for (const child of item.children) {
      collectScreensFromItem(child, out);
    }
  }
  return out;
}

function buildScreenGroups(navItems) {
  const groups = [];
  for (const item of navItems || []) {
    const screens = collectScreensFromItem(item, []);
    if (screens.length === 0) continue;
    const isSingleTopLevel =
      item?.to?.startsWith("/app/") && screens.length === 1 && !item?.children?.length;
    groups.push({
      id: item.to || item.label || String(groups.length),
      groupLabel: isSingleTopLevel ? null : item.label,
      screens,
    });
  }
  return groups;
}

function formatRoleLabel(roleSlug) {
  return String(roleSlug ?? "user")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function useGreeting(hour) {
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  if (hour >= 17 && hour < 21) return "Good evening";
  return "Good night";
}

function useLiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Slim live clock — one line, second-sync progress bar */
function LiveClockStrip({ now }) {
  const hours = pad2(now.getHours());
  const minutes = pad2(now.getMinutes());
  const seconds = pad2(now.getSeconds());
  const tick = (now.getSeconds() / 60) * 100;

  const dateShort = now.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return (
    <div
      className="rd-clock"
      style={{ "--tick": `${tick}%` }}
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="rd-clock__live">
        <span className="rd-clock__dot" aria-hidden />
        Live
      </span>
      <time className="rd-clock__digits" dateTime={now.toISOString()}>
        {hours}
        <span className="rd-clock__sep">:</span>
        {minutes}
        <span className="rd-clock__sep rd-clock__sep--sec">:</span>
        <span className="rd-clock__sec">{seconds}</span>
      </time>
      <span className="rd-clock__date">{dateShort}</span>
      <span className="rd-clock__bar" aria-hidden>
        <span className="rd-clock__bar-fill" />
      </span>
    </div>
  );
}

function IconArrow() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M7.5 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function DynamicRoleDashboard() {
  const roleSlug = useEffectiveRoleSlug();
  const { navItems, loading, error } = useRoleNavigation(roleSlug, Boolean(roleSlug));
  const now = useLiveClock();

  const screenGroups = useMemo(() => buildScreenGroups(navItems), [navItems]);
  const screenCount = useMemo(
    () => screenGroups.reduce((n, g) => n + g.screens.length, 0),
    [screenGroups]
  );
  const roleLabel = formatRoleLabel(roleSlug);
  const greeting = useGreeting(now.getHours());

  const displayName = useMemo(() => {
    if (typeof localStorage === "undefined") return null;
    const payload = decodeJwtPayload(localStorage.getItem("jwt_token"));
    const name = payload?.name != null ? String(payload.name).trim() : "";
    return name || null;
  }, []);

  const firstName = displayName ? displayName.split(/\s+/)[0] : null;

  return (
    <div className="role-dash">
      <header className="rd-bar">
        <div className="rd-bar__left">
          <p className="rd-bar__eyebrow">Dashboard</p>
          <h1 className="rd-bar__title">
            {greeting}
            {firstName ? <span className="rd-bar__name">, {firstName}</span> : null}
          </h1>
        </div>

        <div className="rd-bar__center">
          <span className="rd-chip">{roleLabel}</span>
          <span className="rd-bar__stat">
            <strong>{loading ? "—" : screenCount}</strong> screens
          </span>
          <span className="rd-bar__dot" aria-hidden />
          <span className="rd-bar__stat">
            <strong>{loading ? "—" : navItems.length}</strong> groups
          </span>
        </div>

        <LiveClockStrip now={now} />
      </header>

      <main className="rd-main">
        <div className="rd-main__head">
          <h2 className="rd-main__title">Your applications</h2>
          <p className="rd-main__hint">
            {loading
              ? "Loading modules…"
              : screenCount === 0
                ? "No modules assigned"
                : `${screenCount} module${screenCount === 1 ? "" : "s"} ready`}
          </p>
        </div>

        {loading ? (
          <div className="rd-apps rd-apps--loading" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="rd-app rd-app--skel" />
            ))}
          </div>
        ) : error ? (
          <p className="rd-alert" role="alert">
            {error}
          </p>
        ) : screenCount > 0 ? (
          <div className="rd-apps-wrap">
            {screenGroups.map((group) => (
              <section key={group.id} className="rd-apps-section">
                {group.groupLabel ? (
                  <h3 className="rd-apps-section__label">{group.groupLabel}</h3>
                ) : null}
                <div className="rd-apps">
                  {group.screens.map((link) => (
                    <Link key={link.to} to={link.to} className="rd-app">
                      <span className="rd-app__icon" aria-hidden>
                        <NavRailIcon iconKey={link.iconKey} />
                      </span>
                      <span className="rd-app__body">
                        <span className="rd-app__name">{link.label}</span>
                        <span className="rd-app__open">Open module</span>
                      </span>
                      <span className="rd-app__arrow" aria-hidden>
                        <IconArrow />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="rd-empty">
            <p className="rd-empty__title">Nothing to show yet</p>
            <p className="rd-empty__text">
              Permissions can be configured in User builder. Sign in again after they are saved.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
